<template>
  <div>
    <v-form @submit.prevent ref="form">
      <v-row>
        <v-col cols="12">
          <h4>{{ $t("new-event.recurring-event.subtitle-1") }}</h4>
          <small>{{ $t("new-event.recurring-event.subtitle-2") }}</small>
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-start">
          <DatePicker
            ref="datePickerComponent"
            :label="$t('new-event.recurring-event.dates-event')"
            :multiple="true"
            @formatted-date="updateSelectedDate"
            :clearable="true"
            :rules="[rules.required]"
            :edit="selectedDate"
            hasRange
            hasWeekdaySelector
          />
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-start">
          <TimePicker
            :label="$t('new-event.recurring-event.hours-event')"
            :placeholder="
              $t('new-event.recurring-event.hours-event-placeholder')
            "
            :disabled="false"
            @input="updateTimeSelection"
            :rules="[rules.required]"
            :edit="selectedTime"
            multiple
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="d-flex align-center justify-space-between"
        >
          <div>
            <v-btn
              width="34"
              height="34"
              color="primary"
              class="white--text"
              fab
              @click="addDate()"
            >
              <v-icon size="20" dark>mdi-plus</v-icon>
            </v-btn>
            <v-btn
              width="34"
              height="34"
              color="red"
              class="white--text ml-4"
              fab
              @click="confirmRemove()"
              :disabled="
                getSelectedDateToTickets?.length > 0 &&
                getSelectedDateToTickets[0] == ['all']
              "
            >
              <v-icon size="20">mdi-delete-outline</v-icon>
            </v-btn>
          </div>
          <span
            class="cancel-selection"
            @click="cancelSelection"
            v-if="isMultipleSelection"
            >{{ $t("new-event.buttons.cancel-multiple-selection") }}
            <v-icon color="#572c88">mdi-close</v-icon></span
          >
        </v-col>
        <div
          v-if="availableMonths?.length > 1"
          class="d-flex align-center position-relative px-4"
        >
          <div
            v-for="month in availableMonths"
            :key="month"
            @click="toggleMonthFilter(month)"
            class="filter-btn"
            :class="{ 'active-filter': selectedMonths?.includes(month) }"
          >
            {{ month }}
          </div>
        </div>
        <div
          class="d-flex align-center position-relative px-4"
          style="width: 100%; user-select: none"
        >
          <div
            ref="scrollableCol"
            id="scrollableCol"
            class="d-flex align-center custom-scroll"
            style="
              gap: 12px;
              padding-left: 4px;
              padding-right: 40px;
              padding-top: 12px;
              padding-bottom: 12px;
            "
          >
            <div
              :class="{
                'selected-date': getSelectedDateToTickets?.includes('all'),
                'default-date': !getSelectedDateToTickets?.includes('all'),
              }"
              @click="selectDate('all')"
              class="date-btn"
              v-if="dates?.length > 1 && selectedMonths?.length === 0"
            >
              {{ $t("new-event.recurring-event.all-dates") }}
            </div>
            <div v-for="date in filteredDates" :key="date.rawDate">
              <div
                :class="{
                  'selected-date': getSelectedDateToTickets?.includes(
                    date.rawDate
                  ),
                  'default-date': !getSelectedDateToTickets?.includes(
                    date.rawDate
                  ),
                  'multiple-selection':
                    getSelectedDateToTickets?.includes(date.rawDate) &&
                    isMultipleSelection,
                }"
                @click="handleDateClick($event, date.rawDate)"
                class="date-btn"
              >
                <v-checkbox
                  class="ma-0 pa-0 checkbox-multi-selection"
                  hide-details
                  dense
                  color="primary"
                  :input-value="
                    isMultipleSelection &&
                    getSelectedDateToTickets.includes(date.rawDate)
                  "
                  @click="toggleDateSelection(date.rawDate)"
                />
                <div class="d-flex flex-column">
                  <span class="day-size">{{ date.day }}</span>
                  <span style="max-height: 16px; font-size: 14px"
                    >{{ date.month }}/{{ date.year }}</span
                  >
                  <span
                    style="
                      max-height: 16px;
                      font-size: 14px;
                      font-weight: lighter;
                    "
                    >{{ date.weekday }}</span
                  >
                </div>
                <span style="font-size: 22px">{{ date.time }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="d-flex align-center justify-center position-relative px-4"
          style="width: 100%"
          v-if="hasScroll"
        >
          <template v-for="(button, index) in scrollButtons">
            <v-btn
              :key="index"
              class="hidden-md-and-down btn-scroll ml-4 mr-1 primary"
              @click="button.action"
              min-width="34"
              :disabled="
                ((button.icon === 'mdi-page-first' ||
                  button.icon === 'mdi-chevron-left') &&
                  !canScrollLeft) ||
                ((button.icon === 'mdi-page-last' ||
                  button.icon === 'mdi-chevron-right') &&
                  !canScrollRight)
              "
            >
              <v-icon>{{ button.icon }}</v-icon>
            </v-btn>
          </template>
        </div>
        <v-col>
          <span
            v-html="
              $t('new-event.recurring-event.explanations.keybind-shortcuts')
            "
            style="font-size: 12px"
          />
        </v-col>

        <v-col cols="12" v-if="dates?.length > 0">
          <h4>{{ $t("new-event.recurring-event.create-tickets") }}</h4>
          <h5 class="primary--text">
            {{ explanationTitleDate }}
          </h5>
          <small>
            {{ explanationSubtitleDate }}
          </small>
        </v-col>
      </v-row>
      <ConfirmAction
        :dialog.sync="confirmDelete"
        :title="$t('new-event.recurring-event.remove-title')"
        :subtitle="$t('new-event.recurring-event.remove-subtitle')"
        @action="removeDate"
        @close="cancelRemove"
      />
    </v-form>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import DatePicker from "../forms/DatePicker.vue";
import TimePicker from ".././TimePicker.vue";
import ConfirmAction from "./EventSettingsModal/ConfirmAction.vue";
import mixins from "@/mixins";
const { formRules } = mixins;
export default {
  name: "RecurringEvent",
  components: { DatePicker, TimePicker, ConfirmAction },
  mixins: [formRules],
  data() {
    return {
      selectedDate: [],
      selectedTime: null,
      confirmDelete: false,
      form: null,
      datePickerComponent: null,
      datePickerValue: null,
      hasScroll: false,
      selectedMonth: null,
      selectedMonths: [],
      scrollButtons: [
        { icon: "mdi-page-first", action: this.scrollStart },
        { icon: "mdi-chevron-left", action: this.scrollLeft },
        { icon: "mdi-chevron-right", action: this.scrollRight },
        { icon: "mdi-page-last", action: this.scrollEnd },
      ],
      canScrollLeft: false,
      canScrollRight: false,
      isMultipleSelection: false,
    };
  },
  computed: {
    ...mapGetters([
      "getSelectedDateToTickets",
      "getAllDatesOfTickets",
      "getTicketList",
    ]),
    dates() {
      return this.formatDates(this.getAllDatesOfTickets);
    },
    availableMonths() {
      const months = this.dates.map(date => date.month);
      return [...new Set(months)];
    },
    filteredDates() {
      if (this.selectedMonths?.length === 0) return this.dates;
      return this.dates.filter(date =>
        this.selectedMonths?.includes(date.month)
      );
    },
    explanationTitleDate() {
      if (this.getSelectedDateToTickets?.length === 0) {
        return this.$t("new-event.recurring-event.explanations.none-title");
      }
      if (
        Array.isArray(this.getSelectedDateToTickets) &&
        this.getSelectedDateToTickets.length === 1
      ) {
        if (this.getSelectedDateToTickets[0] === "all") {
          return this.$t("new-event.recurring-event.all-dates");
        }
        const infoDate = this.formatDates(this.getSelectedDateToTickets);
        if (infoDate.length > 0) {
          return `${this.$t("new-event.recurring-event.day")} ${
            infoDate[0].day
          } ${this.$t("new-event.recurring-event.of")} ${
            infoDate[0].month
          } ${this.$t("new-event.recurring-event.of")} ${
            infoDate[0].year
          }, ${this.$t(infoDate[0].weekday.toLowerCase())}.`;
        }
      }
      const selectedCount = Array.isArray(this.getSelectedDateToTickets)
        ? this.getSelectedDateToTickets.length
        : 0;
      return `${this.$t(
        "new-event.recurring-event.to"
      )} ${selectedCount} ${this.$t(
        "new-event.recurring-event.selected-dates"
      )}`;
    },
    explanationSubtitleDate() {
      if (this.getSelectedDateToTickets?.length === 0) {
        return this.$t("new-event.recurring-event.explanations.none");
      }
      if (
        Array.isArray(this.getSelectedDateToTickets) &&
        this.getSelectedDateToTickets.length === 1
      ) {
        if (this.getSelectedDateToTickets[0] === "all") {
          return this.$t("new-event.recurring-event.explanations.all");
        }
        return this.$t("new-event.recurring-event.explanations.one");
      }
      return this.$t("new-event.recurring-event.explanations.more-than-one");
    },
    hasMultipleDatesSelected() {
      return this.getSelectedDateToTickets?.length >= 2;
    },
  },
  methods: {
    ...mapMutations([
      "setDatesOfTickets",
      "setSelectedDateToTickets",
      "addSelectedDateToTickets",
      "addDatesOfTickets",
    ]),
    ...mapActions([
      "showSnackbarSuccess",
      "showSnackbarError",
      "canRemoveDateFromRecurringEvent",
      "removeTicket",
    ]),
    toggleMonthFilter(month) {
      if (this.selectedMonths?.includes(month)) {
        this.selectedMonths = this.selectedMonths?.filter(m => m !== month);
      } else {
        this.selectedMonths?.push(month);
      }
    },
    handleDateClick(event, rawDate) {
      if (event.ctrlKey) {
        this.isMultipleSelection = true;
        this.toggleSelectDate(rawDate);
      } else if (event.shiftKey) {
        this.isMultipleSelection = true;
        this.selectDateRange(rawDate);
      } else if (this.isMultipleSelection) {
        this.toggleSelectDate(rawDate);
      } else {
        this.selectSingleDate(rawDate);
      }
    },
    toggleDateSelection(rawDate) {
      if (!this.isMultipleSelection) {
        this.setSelectedDateToTickets([]);
      }
      this.isMultipleSelection = true;
      const dateAlreadyExists =
        this.getSelectedDateToTickets?.includes(rawDate);
      console.log(dateAlreadyExists);

      setTimeout(() => {
        if (dateAlreadyExists) {
          this.setSelectedDateToTickets([
            ...this.getSelectedDateToTickets.filter(date => date !== rawDate),
          ]);
        } else {
          this.addSelectedDateToTickets(rawDate);
        }
      }, 100);
    },
    toggleSelectDate(rawDate) {
      const index = this.getSelectedDateToTickets.indexOf(rawDate);
      if (index === -1) {
        this.addSelectedDateToTickets(rawDate);
      } else {
        this.getSelectedDateToTickets.splice(index, 1);
      }
      this.lastSelectedIndex = this.dates.findIndex(
        date => date.rawDate === rawDate
      );
    },
    hasScrollOnDates() {
      const scrollableCol = this.$refs.scrollableCol;
      if (!scrollableCol) {
        this.hasScroll = false;
        return;
      }
      this.updateScrollButtonsState();
      this.hasScroll = scrollableCol.scrollWidth > scrollableCol.clientWidth;
    },
    scrollStart() {
      const scrollableCol = this.$refs.scrollableCol;
      if (scrollableCol) {
        scrollableCol.scrollTo({ left: 0, behavior: "smooth" });
      }
    },
    scrollLeft() {
      const scrollableCol = this.$refs.scrollableCol;
      if (scrollableCol) {
        scrollableCol.scrollBy({ left: -800, behavior: "smooth" });
      }
    },
    scrollRight() {
      const scrollableCol = this.$refs.scrollableCol;
      if (scrollableCol) {
        scrollableCol.scrollBy({ left: 800, behavior: "smooth" });
      }
    },
    scrollEnd() {
      const scrollableCol = this.$refs.scrollableCol;
      if (scrollableCol) {
        scrollableCol.scrollTo({
          left: scrollableCol.scrollWidth,
          behavior: "smooth",
        });
      }
    },
    updateScrollButtonsState() {
      const scrollableCol = this.$refs.scrollableCol;
      if (scrollableCol) {
        this.canScrollLeft = scrollableCol.scrollLeft > 0;

        this.canScrollRight =
          scrollableCol.scrollLeft + scrollableCol.clientWidth <
          scrollableCol.scrollWidth;
      }
    },

    selectDate(date) {
      if (!this.getSelectedDateToTickets?.includes(date)) {
        this.setSelectedDateToTickets([date]);
      }
    },
    selectSingleDate(rawDate) {
      this.setSelectedDateToTickets([rawDate]);
      this.lastSelectedIndex = this.dates.findIndex(
        date => date.rawDate === rawDate
      );
    },
    selectDateRange(rawDate) {
      const currentIndex = this.dates.findIndex(
        date => date.rawDate === rawDate
      );
      const start = Math.min(this.lastSelectedIndex, currentIndex);
      const end = Math.max(this.lastSelectedIndex, currentIndex);
      this.setSelectedDateToTickets(
        this.dates.slice(start, end + 1).map(date => date.rawDate)
      );
    },
    updateSelectedDate(date) {
      this.selectedDate = date;
    },
    updateTimeSelection(time) {
      this.selectedTime = time;
    },

    async addDate() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (this.selectedDate.length === 0 || this.selectedTime.length === 0) {
        return this.showSnackbarError(
          this.$t("new-event.recurring-event.select-date-and-time")
        );
      }

      const dateTimeCombinations = this.selectedDate.flatMap(date =>
        this.selectedTime.map(time => `${date} ${time}`)
      );

      const exists = dateTimeCombinations.some(combination =>
        this.getAllDatesOfTickets.includes(combination)
      );
      if (exists) {
        return this.showSnackbarError(
          this.$t("new-event.recurring-event.already-added")
        );
      }

      this.addDatesOfTickets(dateTimeCombinations);

      if (this.getAllDatesOfTickets.length > 1) {
        this.setSelectedDateToTickets(["all"]);
      } else {
        this.setSelectedDateToTickets([this.getAllDatesOfTickets[0]]);
      }

      this.selectedDate = [];
      this.selectedTime = [];
      this.$nextTick(() => {
        this.$refs.datePickerComponent?.clearDates();
      });

      this.$refs.form.reset();
    },
    formatDates(dates) {
      return dates
        ?.map(dateString => {
          const date = new Date(dateString);

          const day = date.getDate().toString().padStart(2, "0");
          const month = this.$t(
            `new-event.recurring-event.months.${date
              .toLocaleString("en-US", { month: "long" })
              .toLowerCase()}`
          );
          const year = date.getFullYear();
          const time = date.toTimeString().slice(0, 5);
          const weekday = this.$t(
            `new-event.recurring-event.weekdays.${date
              .toLocaleString("en-US", { weekday: "long" })
              .toLowerCase()}`
          );

          return {
            day,
            month,
            year,
            time,
            weekday,
            rawDate: dateString,
            isHover: false,
          };
        })
        .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
    },

    async removeDate() {
      try {
        this.confirmDelete = false;
        const ticketsToRemove = this.getTicketList?.filter(ticket =>
          this.getSelectedDateToTickets.includes(ticket.ticketDate)
        );
        for (const ticket of ticketsToRemove) {
          await this.removeTicket(ticket);
        }
        const remainingDates = this.getAllDatesOfTickets?.filter(
          date => !this.getSelectedDateToTickets.includes(date)
        );
        this.setDatesOfTickets(remainingDates);
        if (remainingDates.length === 0) {
          this.setSelectedDateToTickets([]);
        } else if (remainingDates.length === 1) {
          this.setSelectedDateToTickets([remainingDates[0]]);
        } else {
          this.setSelectedDateToTickets(["all"]);
        }
        this.dateToDelete = null;
        this.showSnackbarSuccess(
          this.$t("new-event.recurring-event.success-remove-date")
        );
      } catch (error) {
        this.showSnackbarError(
          this.$t("new-event.recurring-event.error-remove-date")
        );
      }
    },

    async confirmRemove() {
      try {
        for (const date of this.getSelectedDateToTickets) {
          await this.canRemoveDateFromRecurringEvent(date);
        }
        this.confirmDelete = true;
      } catch (error) {
        this.showSnackbarError(
          this.$t("new-event.recurring-event.error-remove-date")
        );
      }
    },

    cancelRemove() {
      this.confirmDelete = false;
    },
    cancelSelection() {
      this.isMultipleSelection = false;
      this.setSelectedDateToTickets([this.getSelectedDateToTickets[0]]);
    },
  },
  watch: {
    getAllDatesOfTickets() {
      this.$nextTick(() => {
        this.hasScrollOnDates();
      });
    },
  },
  mounted() {
    this.hasScrollOnDates();
    window.addEventListener("resize", this.hasScrollOnDates);

    const scrollableCol = this.$refs.scrollableCol;
    if (scrollableCol) {
      scrollableCol.addEventListener("scroll", this.updateScrollButtonsState);
    }
    this.$nextTick(() => {
      this.hasScrollOnDates();
    });
    if (this.getAllDatesOfTickets.length > 0) {
      if (this.getAllDatesOfTickets.length > 1) {
        this.setSelectedDateToTickets(["all"]);
      } else {
        this.setSelectedDateToTickets([this.getAllDatesOfTickets[0]]);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.hasScrollOnDates);

    const scrollableCol = this.$refs.scrollableCol;
    if (scrollableCol) {
      scrollableCol.removeEventListener(
        "scroll",
        this.updateScrollButtonsState
      );
    }
  },
};
</script>

<style scoped>
.date-btn {
  position: relative;
  cursor: pointer;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #6c23a3;
  background-color: white;
  border-radius: 20px;
  padding: 4px;
  height: 120px;
  width: 140px;
  min-height: 120px;
  min-width: 140px;
  box-shadow: 2px 2px 6px #858484, -2px -2px 6px #ffffff;
}

.selection-btn {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
  background-color: #f7f7fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 14px;
  width: 14px;
}

.selection-icon {
  cursor: pointer;
  z-index: 1;
}

.day-size {
  font-size: 42px;
  max-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.default-date {
  background-color: #f7f7fa !important;
  color: #6c23a3 !important;
}

.selected-date {
  background-color: #6c23a3 !important;
  color: white !important;
}

.btn-scroll {
  height: 100% !important;
  display: flex;
  width: 30px !important;
}

.custom-scroll {
  overflow-x: auto;
  padding-inline: 18px;
}

.filter-btn {
  padding: 4px 8px;
  margin-right: 8px;
  cursor: pointer;
  border: 1px solid #6c23a3;
  border-radius: 4px;
  color: #572c88;
}

.active-filter {
  background-color: #572c88;
  color: white;
}

.checkbox-multi-selection {
  position: absolute;
  right: -4px;
  top: 2px;
}

.cancel-selection {
  padding: 4px;
  cursor: pointer;
  border: 1px solid #572c88;
  color: #572c88;
  background-color: #e9e8ed;
  border-radius: 20px;
  padding-inline: 10px;
}

.date-btn {
  border: 2px solid transparent;
  transition: border-color 0.3s ease;
}

.multiple-selection {
  color: #6c23a3 !important;
  background-color: white !important;
  border-color: purple !important;
}

@media (min-width: 960px) {
  .custom-scroll {
    overflow-x: hidden;
    padding-inline: 0px;
  }
}
</style>
